import AOS from "aos";
import "aos/dist/aos.css";
import { useEffect, useMemo, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import { Link, useNavigate, useParams } from "react-router-dom";
import Cursor from "../../common/Cursor";
import Footer from "../../common/Footer";
import Header from "../../common/Header";
import Loader from "../../common/Loader";
import ProgressCircle from "../../common/ProgressCircle";
import { fetchData, postFormData } from "../../api/api";

function stripHtml(html = "") {
  const el = document.createElement("div");
  el.innerHTML = html;
  return (el.textContent || el.innerText || "").trim();
}

function formatLocation(location) {
  if (!location) return "";
  const parts = [];
  if (location.city) parts.push(location.city);
  if (location.country) parts.push(location.country);
  if (location.is_remote) parts.push("Remote");
  return parts.join(", ");
}

function extractHtmlListItems(html = "") {
  if (!html) return [];
  if (!/<li[\s>]/i.test(html)) return [];
  const items = [];
  const re = /<li[^>]*>([\s\S]*?)<\/li>/gi;
  let m;
  while ((m = re.exec(html))) {
    const t = stripHtml(m[1]);
    if (t) items.push(t);
  }
  return items;
}

function splitLines(textOrHtml = "") {
  const fromLi = extractHtmlListItems(textOrHtml);
  if (fromLi.length) return fromLi;

  const clean = stripHtml(textOrHtml);
  const parts = clean
    .split(/\r?\n|•|·|\u2022|-\s+/g)
    .map((s) => s.trim())
    .filter(Boolean);

  // If backend saved as a single paragraph, still show it as a single bullet.
  return parts.length ? parts : clean ? [clean] : [];
}

const CareerApply = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();

  const normalizedJobId = useMemo(() => String(jobId || "").trim(), [jobId]);

  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [portfolioLink, setPortfolioLink] = useState("");
  const [coverLetter, setCoverLetter] = useState("");
  const [resumeFile, setResumeFile] = useState(null);
  const resumeInputRef = useRef(null);

  useEffect(() => {
    AOS.init({ duration: 1200 });
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      setError("");
      setJob(null);
      try {
        if (!normalizedJobId) throw new Error("Missing job id.");
        const data = await fetchData(`/career/job-postings/${normalizedJobId}/`);
        if (alive) setJob(data);
      } catch (e) {
        console.error("Error fetching job posting:", e);
        if (alive) setError("Failed to load job posting details.");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [normalizedJobId]);

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");
    setSuccess("");
    setSubmitted(false);

    if (!normalizedJobId) {
      setError("Job id is missing.");
      return;
    }
    if (!fullName.trim()) {
      setError("Full name is required.");
      return;
    }
    if (!email.trim()) {
      setError("Email is required.");
      return;
    }

    const fd = new FormData();
    // Backend expects the job posting id under `job`
    fd.append("job", normalizedJobId);
    fd.append("full_name", fullName.trim());
    fd.append("email", email.trim());
    if (phone.trim()) fd.append("phone", phone.trim());
    if (portfolioLink.trim()) fd.append("portfolio_link", portfolioLink.trim());
    if (coverLetter.trim()) fd.append("cover_letter", coverLetter.trim());
    if (resumeFile) fd.append("resume", resumeFile);

    try {
      setSubmitLoading(true);
      await postFormData("/career/applications/", fd);
      setSuccess("Application submitted successfully.");
      setSubmitted(true);
      // clear form
      setFullName("");
      setEmail("");
      setPhone("");
      setPortfolioLink("");
      setCoverLetter("");
      setResumeFile(null);
      if (resumeInputRef.current) resumeInputRef.current.value = "";
    } catch (err) {
      console.error("Application submit failed:", err);
      setError(err?.message || "Failed to submit application.");
    } finally {
      setSubmitLoading(false);
    }
  }

  return (
    <>
      <Helmet>
        <title>{job?.title ? `Apply | ${job.title}` : "Apply | Careers"} | Marichi Tech AI</title>
      </Helmet>

      <div>
        <Cursor />
        <Header />

        <section style={{ padding: "60px 0 20px" }}>
          <div className="container" data-aos="fade-up">
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px", flexWrap: "wrap" }}>
              <h1 style={{ color: "white", fontSize: "clamp(24px, 4vw, 34px)", margin: 0 }}>
                {job?.title || "Apply"}
              </h1>
              <Link to="/career" style={{ color: "#60a5fa", textDecoration: "none", fontWeight: 600 }}>
                ← Back to careers
              </Link>
            </div>
            {(job?.location || job?.job_type) && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginTop: 12 }}>
                {formatLocation(job?.location) && (
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      padding: "6px 10px",
                      borderRadius: "999px",
                      background: "rgba(96, 165, 250, 0.10)",
                      border: "1px solid rgba(96, 165, 250, 0.25)",
                      color: "#93c5fd",
                      fontSize: "13px",
                      fontWeight: 600,
                    }}
                  >
                    {formatLocation(job.location)}
                  </span>
                )}
                {job?.job_type?.type_name && (
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      padding: "6px 10px",
                      borderRadius: "999px",
                      background: "rgba(34, 197, 94, 0.10)",
                      border: "1px solid rgba(34, 197, 94, 0.25)",
                      color: "#86efac",
                      fontSize: "13px",
                      fontWeight: 600,
                    }}
                  >
                    {job.job_type.type_name}
                  </span>
                )}
              </div>
            )}
            {job?.short_description && (
              <p style={{ color: "rgba(255,255,255,0.85)", marginTop: 12, marginBottom: 0, lineHeight: 1.7 }}>
                {stripHtml(job.short_description)}
              </p>
            )}

            {(stripHtml(job?.responsibilities) || stripHtml(job?.requirements) || stripHtml(job?.preferred_skills)) && (
              <div
                style={{
                  marginTop: 16,
                  padding: "16px 16px",
                  borderRadius: 14,
                  background: "rgba(255, 255, 255, 0.03)",
                  border: "1px solid rgba(255, 255, 255, 0.10)",
                }}
              >
                {stripHtml(job?.responsibilities) && (
                  <div style={{ marginBottom: 14 }}>
                    <div style={{ color: "white", fontWeight: 800, marginBottom: 8 }}>Responsibilities</div>
                    <ul style={{ margin: 0, paddingLeft: 18, color: "rgba(255,255,255,0.85)", lineHeight: 1.7 }}>
                      {splitLines(job.responsibilities).map((line, idx) => (
                        <li key={`resp-${idx}`}>{line}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {stripHtml(job?.requirements) && (
                  <div style={{ marginBottom: 14 }}>
                    <div style={{ color: "white", fontWeight: 800, marginBottom: 8 }}>Requirements</div>
                    <ul style={{ margin: 0, paddingLeft: 18, color: "rgba(255,255,255,0.85)", lineHeight: 1.7 }}>
                      {splitLines(job.requirements).map((line, idx) => (
                        <li key={`req-${idx}`}>{line}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {stripHtml(job?.preferred_skills) && (
                  <div>
                    <div style={{ color: "white", fontWeight: 800, marginBottom: 8 }}>Preferred Skills</div>
                    <ul style={{ margin: 0, paddingLeft: 18, color: "rgba(255,255,255,0.85)", lineHeight: 1.7 }}>
                      {splitLines(job.preferred_skills).map((line, idx) => (
                        <li key={`skill-${idx}`}>{line}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        </section>

        {loading && <Loader />}

        <section style={{ padding: "20px 0 60px" }}>
          <div className="container">
            {!loading && error && (
              <div style={{ padding: "18px 16px", borderRadius: "10px", border: "1px solid rgba(239, 68, 68, 0.4)", background: "rgba(239, 68, 68, 0.08)", color: "#fecaca" }}>
                {error}
              </div>
            )}

            {!loading && !error && (
              <form onSubmit={handleSubmit} style={{ maxWidth: 820, margin: "0 auto" }} data-aos="fade-up">
                <div
                  style={{
                    borderRadius: "14px",
                    padding: "22px",
                    background: "rgba(255, 255, 255, 0.03)",
                    border: "1px solid rgba(255, 255, 255, 0.10)",
                    boxShadow: "0 10px 30px rgba(0,0,0,0.15)",
                  }}
                >
                  <h2 style={{ marginTop: 0, marginBottom: 14, color: "white", fontSize: "22px" }}>
                    Application Form
                  </h2>
                  {success && (
                    <div style={{ marginBottom: 14, padding: "12px 14px", borderRadius: 10, border: "1px solid rgba(34, 197, 94, 0.35)", background: "rgba(34, 197, 94, 0.10)", color: "#bbf7d0" }}>
                      {success}
                    </div>
                  )}
                  {error && (
                    <div style={{ marginBottom: 14, padding: "12px 14px", borderRadius: 10, border: "1px solid rgba(239, 68, 68, 0.4)", background: "rgba(239, 68, 68, 0.08)", color: "#fecaca" }}>
                      {error}
                    </div>
                  )}

                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "14px" }}>
                    <label style={{ display: "block", color: "white" }}>
                      Full Name <span style={{ color: "#fca5a5" }}>*</span>
                      <input
                        value={fullName}
                        onChange={(e) => setFullName(e.target.value)}
                        placeholder="Your full name"
                        style={{ width: "100%", marginTop: 8, padding: "12px 12px", borderRadius: 10, border: "1px solid rgba(255,255,255,0.18)", background: "rgba(0,0,0,0.25)", color: "white" }}
                        autoComplete="name"
                      />
                    </label>

                    <label style={{ display: "block", color: "white" }}>
                      Email <span style={{ color: "#fca5a5" }}>*</span>
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="you@example.com"
                        style={{ width: "100%", marginTop: 8, padding: "12px 12px", borderRadius: 10, border: "1px solid rgba(255,255,255,0.18)", background: "rgba(0,0,0,0.25)", color: "white" }}
                        autoComplete="email"
                      />
                    </label>
                  </div>

                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "14px", marginTop: 14 }}>
                    <label style={{ display: "block", color: "white" }}>
                      Phone
                      <input
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        placeholder="Optional"
                        style={{ width: "100%", marginTop: 8, padding: "12px 12px", borderRadius: 10, border: "1px solid rgba(255,255,255,0.18)", background: "rgba(0,0,0,0.25)", color: "white" }}
                        autoComplete="tel"
                      />
                    </label>

                    <label style={{ display: "block", color: "white" }}>
                      Portfolio Link
                      <input
                        value={portfolioLink}
                        onChange={(e) => setPortfolioLink(e.target.value)}
                        placeholder="https://..."
                        style={{ width: "100%", marginTop: 8, padding: "12px 12px", borderRadius: 10, border: "1px solid rgba(255,255,255,0.18)", background: "rgba(0,0,0,0.25)", color: "white" }}
                      />
                    </label>
                  </div>

                  <div style={{ marginTop: 14 }}>
                    <label style={{ display: "block", color: "white" }}>
                      Resume (PDF/DOC)
                      <input
                        type="file"
                        ref={resumeInputRef}
                        onChange={(e) => setResumeFile(e.target.files?.[0] || null)}
                        style={{ width: "100%", marginTop: 8, padding: "10px 12px", borderRadius: 10, border: "1px solid rgba(255,255,255,0.18)", background: "rgba(0,0,0,0.25)", color: "white" }}
                      />
                    </label>
                  </div>

                  <div style={{ marginTop: 14 }}>
                    <label style={{ display: "block", color: "white" }}>
                      Cover Letter
                      <textarea
                        value={coverLetter}
                        onChange={(e) => setCoverLetter(e.target.value)}
                        placeholder="Optional"
                        rows={6}
                        style={{ width: "100%", marginTop: 8, padding: "12px 12px", borderRadius: 10, border: "1px solid rgba(255,255,255,0.18)", background: "rgba(0,0,0,0.25)", color: "white", resize: "vertical" }}
                      />
                    </label>
                  </div>

                  <div style={{ marginTop: 18, display: "flex", justifyContent: "flex-end" }}>
                    {submitted && (
                      <button
                        type="button"
                        onClick={() => navigate("/career")}
                        style={{
                          padding: "12px 14px",
                          borderRadius: 10,
                          border: "1px solid rgba(255,255,255,0.18)",
                          background: "rgba(0,0,0,0.25)",
                          color: "white",
                          fontWeight: 700,
                          cursor: "pointer",
                          marginRight: 10,
                        }}
                      >
                        Back to careers
                      </button>
                    )}
                    <button
                      type="submit"
                      disabled={submitLoading || submitted}
                      style={{
                        padding: "12px 20px",
                        borderRadius: 10,
                        border: "none",
                        background: submitLoading || submitted ? "rgba(37, 99, 235, 0.6)" : "#2563eb",
                        color: "white",
                        fontWeight: 700,
                        cursor: submitLoading || submitted ? "not-allowed" : "pointer",
                      }}
                    >
                      {submitLoading ? "Submitting..." : submitted ? "Submitted" : "Submit Application"}
                    </button>
                  </div>
                </div>
              </form>
            )}
          </div>
        </section>

        <Footer />
        <ProgressCircle />
      </div>
    </>
  );
};

export default CareerApply;

